export const Tooltips = {
  // ===== Promise Health Score =====
  promiseIntegrityScore: {
    title: "Promise Integrity Score",
    body:
      "A 0–100 score based on weighted promise breaks in the selected time window. Higher is better. " +
      "Severe events (cancellations, hard backorders, late/partial) weigh more than minor issues."
  },

  scoreDelta: {
    title: "Score change",
    body:
      "Shows how your score moved vs the previous equivalent window. " +
      "Use it to validate whether changes and playbooks are improving reliability."
  },

  // ===== Key Metrics =====
  totalPromises: {
    title: "Total Promises",
    body:
      "Number of orders observed in this window. Each order represents a customer promise (availability, delivery, or pickup expectation)."
  },

  brokenRate: {
    title: "Broken Rate",
    body:
      "Weighted promise breaks per promise. Severity-adjusted: high-impact events count more than low-impact events."
  },

  changeRate: {
    title: "Change Rate",
    body:
      "How often promise conditions changed after checkout (date shifts, split/ship-complete changes, reroutes). " +
      "High change rate usually indicates weak available-to-promise or operational volatility."
  },

  urgentMissRate: {
    title: "Urgent Miss Rate",
    body:
      "Percentage of urgency-tagged orders that missed their stated deadline. " +
      "This requires urgency tagging (e.g., gift/life-event deadline) to be enabled."
  },

  // ===== Drivers =====
  driverCancellations: {
    title: "Cancellations",
    body:
      "Orders canceled after confirmation. Typically the highest trust + margin damage driver. " +
      "Often caused by inventory mismatch, oversells, or upstream delays."
  },

  driverBackorders: {
    title: "Backorders",
    body:
      "Orders accepted without reliable availability. Backorders are often a leading indicator of inventory promise failure."
  },

  driverPromiseBreaks: {
    title: "Promise Breaks",
    body:
      "Delivery/fulfillment misses relative to what customers expected. Often tied to carrier performance, cutoffs, or fulfillment delays."
  },

  driverLatePartial: {
    title: "Late / Partial",
    body:
      "Partial shipments or late fulfillment that trigger repeat contacts, refunds, or churn risk. " +
      "Common with multi-node sourcing and ship-complete constraints."
  },

  driverSupport: {
    title: "Support Cost",
    body:
      "Estimated support burden driven by disappointment events. This is modeled using your assumptions (ticket cost, tickets per event)."
  },

  weightedImpact: {
    title: "Weighted Impact",
    body:
      "Impact score that weights event count by severity. Helps prioritize fixes that reduce trust damage fastest."
  },

  ratePer100: {
    title: "Rate per 100 promises",
    body:
      "Event frequency normalized by order volume. Useful to compare across time windows and with peers."
  },

  // ===== Playbooks =====
  playbookAdopted: {
    title: "Mark as adopted",
    body:
      "Logs that you implemented this playbook. We track score and drivers over time to validate improvement."
  },

  createTask: {
    title: "Create task",
    body:
      "Creates a lightweight task you can assign internally. Use it to drive follow-through and measure outcomes."
  },

  requestSlackApproval: {
    title: "Request Slack approval",
    body:
      "Routes this exception into Slack for approve/reject decisions. Requires Slack connection and a default channel."
  },

  // ===== Trends =====
  trendsEventsOverTime: {
    title: "Events over time",
    body:
      "Promise breaks over time segmented by severity (low/medium/high). " +
      "Use this to confirm whether reliability is improving week over week."
  },

  trendsDriverDistribution: {
    title: "Driver distribution",
    body:
      "Breakdown of which event types dominate your reliability profile. " +
      "A healthy system usually has low cancellations and controlled backorders."
  },

  severityHigh: {
    title: "High severity",
    body:
      "Highest trust and margin damage. Prioritize fixing these first."
  },

  severityMed: {
    title: "Medium severity",
    body:
      "Meaningful operational friction. Often creates support load and comp costs."
  },

  severityLow: {
    title: "Low severity",
    body:
      "Smaller issues that matter at scale. Use playbooks to reduce them over time."
  },

  // ===== Benchmark =====
  benchmarkPrivacy: {
    title: "Benchmark privacy",
    body:
      "We only publish benchmarks for cohorts with a minimum number of similar merchants (k-anonymity). " +
      "This protects privacy and keeps benchmarks statistically meaningful."
  },

  benchmarkPosition: {
    title: "Benchmark position",
    body:
      "Your score relative to cohort percentiles (p25/p50/p75). " +
      "Top quartile indicates stronger promise reliability vs similar merchants."
  },

  cohortKey: {
    title: "Cohort",
    body:
      "Your peer group based on order volume and AOV bands. Cohort membership is used only for aggregated percentiles."
  },

  // ===== Impact =====
  estimatedMonthlyLoss: {
    title: "Estimated Monthly Loss",
    body:
      "Estimated leakage from cancellations, backorders, promise breaks, and support. " +
      "This is modeled using your assumptions (AOV, gross margin %, ticket cost)."
  },

  potentialSavings: {
    title: "Potential Savings",
    body:
      "Projected savings if playbooks reduce promise breaks by your expected reduction %. " +
      "Tune assumptions to match your business."
  },

  annualSavingsPotential: {
    title: "Annual Savings Potential",
    body:
      "Estimated yearly savings based on current trend and assumptions. Use weekly digests to validate progress."
  },

  impactAssumptions: {
    title: "Assumptions",
    body:
      "These drive the impact estimate. You can edit them to better match your business: AOV, gross margin %, support cost per ticket, tickets per disappointment, and expected reduction."
  },

  costBreakdown: {
    title: "Cost breakdown",
    body:
      "Estimated where your loss is coming from. Use this to prioritize the driver that creates the biggest financial impact."
  },

  // ===== Slack / Approvals =====
  slackConnected: {
    title: "Slack connected",
    body:
      "Approvals and decision traces can flow into Slack. Set a default channel to avoid pasting channel IDs."
  },

  pendingApprovals: {
    title: "Pending approvals",
    body:
      "Decision traces awaiting approval in Slack. Snoozed items are excluded from this count."
  },

  snooze: {
    title: "Snooze",
    body:
      "Hide this approval temporarily. Snoozed approvals won't appear in the queue until they return."
  },

  bulkRerequest: {
    title: "Bulk re-request",
    body:
      "Re-requests selected approvals in Slack at a safe rate to avoid spam and Slack rate limits."
  },

  // ===== Share/Distribution =====
  shareReport: {
    title: "Share report",
    body:
      "Generate a shareable Promise Health snapshot for your team. Great for weekly ops reviews."
  },

  sendToSlack: {
    title: "Send to Slack",
    body:
      "Posts this snapshot to your default Slack channel. Requires Slack connection + default channel."
  }
};
